import { normalizeGroupIds, normalizeRedirectUris, ValidationError } from '@core/validation';
import { MAX_GROUP_IDS } from '@core/types';
import { useState } from 'react';
import { UnrestrictedCallout } from './AccessBadge';

export interface AppFormValues {
  appName: string;
  redirectUris: string[];
  requiredGroupIds: string[];
}

interface AppFormProps {
  initial: AppFormValues;
  /** App names are immutable — Cognito's ClientName and the registry's GSI key both derive from it. */
  nameEditable: boolean;
  submitLabel: string;
  busy: boolean;
  onSubmit: (values: AppFormValues) => void;
}

/** Splits a textarea into non-empty trimmed lines. */
function lines(value: string): string[] {
  return value
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

/**
 * Runs a core normalizer for preview purposes, returning either the normalized values or the exact
 * error message the API would produce. Using the same functions the server uses is the whole point
 * of src/core/validation.ts being dependency-free — a reimplementation here would drift.
 */
function preview<T>(fn: (input: T) => string[], input: T): { values?: string[]; error?: string } {
  try {
    return { values: fn(input) };
  } catch (err) {
    if (err instanceof ValidationError) return { error: err.message };
    throw err;
  }
}

export function AppForm({ initial, nameEditable, submitLabel, busy, onSubmit }: AppFormProps) {
  const [appName, setAppName] = useState(initial.appName);
  const [redirectText, setRedirectText] = useState(initial.redirectUris.join('\n'));
  const [groupText, setGroupText] = useState(initial.requiredGroupIds.join('\n'));
  const [acknowledgedOpen, setAcknowledgedOpen] = useState(false);

  const redirectUris = lines(redirectText);
  const groupIds = lines(groupText);

  const redirectPreview = preview(normalizeRedirectUris, redirectUris);
  const groupPreview = preview(normalizeGroupIds, groupIds);

  const isOpenToEveryone = groupIds.length === 0;
  const blocked =
    busy ||
    !!redirectPreview.error ||
    !!groupPreview.error ||
    (isOpenToEveryone && !acknowledgedOpen);

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        if (blocked) return;
        onSubmit({ appName: appName.trim(), redirectUris, requiredGroupIds: groupIds });
      }}
    >
      <label className="field">
        <span>App name</span>
        <input
          value={appName}
          onChange={(e) => setAppName(e.target.value)}
          disabled={!nameEditable || busy}
          placeholder="forward-summit"
          autoComplete="off"
        />
        <span className="hint">
          {nameEditable
            ? 'Lowercase letters, digits and hyphens.'
            : 'The app name cannot be changed after creation.'}
        </span>
      </label>

      <label className="field">
        <span>Redirect URIs</span>
        <textarea
          value={redirectText}
          onChange={(e) => setRedirectText(e.target.value)}
          disabled={busy}
          rows={4}
          placeholder="https://forward-summit.replit.app/auth/callback"
        />
        <span className="hint">One per line. https only, except localhost. No fragments.</span>
      </label>

      {/*
        Shows the normalized form before submission. new URL().toString() appends a trailing slash
        to a bare origin, and Cognito matches redirect_uri as an exact string — so seeing the change
        here beats discovering it as a redirect_mismatch after saving.
      */}
      {redirectPreview.error ? (
        <div className="callout callout-error">{redirectPreview.error}</div>
      ) : (
        redirectPreview.values &&
        redirectPreview.values.join('\n') !== redirectUris.join('\n') && (
          <div className="callout">
            <strong>Will be stored as:</strong>
            <ul className="plain">
              {redirectPreview.values.map((uri) => (
                <li key={uri}>
                  <code>{uri}</code>
                </li>
              ))}
            </ul>
          </div>
        )
      )}

      <label className="field">
        <span>Required Entra groups</span>
        <textarea
          value={groupText}
          onChange={(e) => setGroupText(e.target.value)}
          disabled={busy}
          rows={4}
          placeholder="8f14e45f-ceea-467a-9f0b-1c0e5b3d2a11"
        />
        <span className="hint">
          One group object ID per line, at most {MAX_GROUP_IDS}. Leave empty to allow any
          authenticated user.
        </span>
      </label>

      {groupPreview.error && <div className="callout callout-error">{groupPreview.error}</div>}

      {/*
        The checkbox gates submit only when the list is empty. Clearing the restriction stays
        possible — it is a legitimate configuration — but it cannot happen by leaving a field blank
        and not noticing.
      */}
      {isOpenToEveryone && (
        <>
          <UnrestrictedCallout />
          <label className="checkbox">
            <input
              type="checkbox"
              checked={acknowledgedOpen}
              onChange={(e) => setAcknowledgedOpen(e.target.checked)}
              disabled={busy}
            />
            <span>I understand this app will be open to everyone in the tenant.</span>
          </label>
        </>
      )}

      <div className="actions">
        <button type="submit" className="primary" disabled={blocked}>
          {busy ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
